/**
 * Component to highlight matching text in search results
 */
import styled from 'styled-components';

interface HighlightTextProps {
    text: string;
    searchTerm: string;
}

export const HighlightText: React.FC<HighlightTextProps> = ({ text, searchTerm }) => {
    if (!searchTerm || searchTerm.length < 2) {
        return <>{text}</>;
    }

    // Split search term into words for multi-word search
    const searchWords = searchTerm
        .toLowerCase()
        .split(/\s+/)
        .filter((word) => word.length >= 2);

    if (searchWords.length === 0) {
        return <>{text}</>;
    }

    // Check if any search word is in the text
    const lowerText = text.toLowerCase();
    const matchingWords = searchWords.filter((word) => lowerText.includes(word));

    if (matchingWords.length === 0) {
        return <>{text}</>;
    }

    // Create a map of positions to highlight
    const highlightPositions: { start: number; end: number }[] = [];

    // Find all occurrences of each matching word
    matchingWords.forEach((word) => {
        let pos = 0;
        while ((pos = lowerText.indexOf(word, pos)) !== -1) {
            highlightPositions.push({
                start: pos,
                end: pos + word.length,
            });
            pos += word.length;
        }
    });

    // Sort positions by start index
    highlightPositions.sort((a, b) => a.start - b.start);

    // Merge overlapping positions
    const mergedPositions: { start: number; end: number }[] = [];
    highlightPositions.forEach((pos) => {
        if (mergedPositions.length === 0) {
            mergedPositions.push(pos);
            return;
        }

        const lastPos = mergedPositions[mergedPositions.length - 1];
        if (pos.start <= lastPos.end) {
            // Overlapping positions, merge them
            lastPos.end = Math.max(lastPos.end, pos.end);
        } else {
            // Non-overlapping, add as new position
            mergedPositions.push(pos);
        }
    });

    // Build the highlighted text
    const parts: React.ReactNode[] = [];
    let lastEnd = 0;

    mergedPositions.forEach((pos, index) => {
        // Add text before the highlighted part
        if (pos.start > lastEnd) {
            parts.push(text.substring(lastEnd, pos.start));
        }

        // Add the highlighted part
        parts.push(<HighlightedSpan key={`highlight-${index}`}>{text.substring(pos.start, pos.end)}</HighlightedSpan>);

        lastEnd = pos.end;
    });

    // Add any remaining text
    if (lastEnd < text.length) {
        parts.push(text.substring(lastEnd));
    }

    return <>{parts}</>;
};

const HighlightedSpan = styled.span`
    font-weight: bold;
`;
